import {
  createSharedStateMessages,
  createSharedStateMessage,
  SB,
} from '@play-co/replicant';
import {
  holdersSchema,
  Holding,
  onchainHoldersSharedStateSchema,
} from './onchainHolders.schema';
import { HP } from '../../lib/HighPrecision';
import { MAX_TOP_HOLDERS } from './onchainHolders.ruleset';

// @warning: never remove/rename shared state messages
export const onchainHoldersMessages = createSharedStateMessages(
  onchainHoldersSharedStateSchema,
)({
  createHolders: createSharedStateMessage(
    SB.object({
      memeId: SB.string(),
    }),
    (state, { memeId }, meta) => {
      state.global.memeId = memeId;
    },
  ),
  updateHolders: createSharedStateMessage(
    SB.object({
      holdingUpdates: holdersSchema,
    }),
    (
      state,
      {
        holdingUpdates,
      }: {
        holdingUpdates: Holding[];
      },
      meta,
    ) => {
      // @perf: in order to keep infra usage low this method was optimized for efficiency

      // Create a map for quick access to holdingUpdates by walletAddress
      const updatesMap = new Map(
        holdingUpdates.map((holdingUpdate) => [
          holdingUpdate.walletAddress,
          holdingUpdate,
        ]),
      );

      const updatedNonZeroHolders: Holding[] = [];

      // Add new holders from holdingUpdates that were not in the current holders
      holdingUpdates.forEach((holdingUpdate) => {
        if (HP(holdingUpdate.tokenAmount).gt(0)) {
          updatedNonZeroHolders.push(holdingUpdate);
        }
      });

      updatedNonZeroHolders.sort((a, b) =>
        HP(b.tokenAmount).minus(a.tokenAmount).toNumber(),
      );

      // Apply updates and create a new holders array
      const updatedHoldersList: Holding[] = [];
      let updatedIndex = 0;

      state.global.holders.forEach((holder) => {
        const holdingAmount = updatesMap.get(holder.walletAddress);
        if (holdingAmount !== undefined) {
          // player holdings updated, will be added at different position
          return;
        }

        // push updatedNonZeroHolders with holdings greater than holder
        while (
          updatedIndex < updatedNonZeroHolders.length &&
          HP(updatedNonZeroHolders[updatedIndex].tokenAmount).gt(
            HP(holder.tokenAmount),
          )
        ) {
          updatedHoldersList.push(updatedNonZeroHolders[updatedIndex]);
          updatedIndex++;
        }

        // player holdings unchanged
        updatedHoldersList.push(holder);
      });

      // add remaining non-zero holders
      while (updatedIndex < updatedNonZeroHolders.length) {
        updatedHoldersList.push(updatedNonZeroHolders[updatedIndex]);
        updatedIndex++;
      }

      // Limit the size of the holders array
      state.global.holders = updatedHoldersList.slice(0, MAX_TOP_HOLDERS);
    },
  ),
});
